import React from 'react';
import ReactTable from 'react-table';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import { Button, Col, Input, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { apiPost, apiRequest } from '../../utils/request';
import { mustBeArray } from '../../utils/lib';
import { BASE_URL, DEFAULT_DATE_FORMAT } from '../../constants';

const formatDateTime = (value) => (value ? moment(value).format('DD-MM-YYYY LT') : '');
const formatLitres = (value) => (value || value === 0 ? `${value}L` : '');

// Archive filter options. Archived receipts stay listed here by design — archiving only hides them
// from the driver-facing screens (receipt picker + bottom-of-run list).
// UNALLOCATED narrows to the receipts that can actually be archived; filtering on "not archived"
// alone would return almost everything, which is no different to ALL.
const ARCHIVE_FILTERS = {
    ALL: 'all',
    UNALLOCATED: 'unallocated',
    ARCHIVED: 'archived',
};

// Receipts land against a truck faster than office staff work through them, so default the grid to a
// recent window rather than the whole history.
const DEFAULT_DAYS_BACK = 14;
const defaultDateFrom = () => moment().subtract(DEFAULT_DAYS_BACK, 'days').format(DEFAULT_DATE_FORMAT);
const defaultDateTo = () => moment().format(DEFAULT_DATE_FORMAT);

// Only unallocated receipts can be archived: one already tied to a run/pickup is the evidence for
// that pickup's volume, so it has to keep showing on the run.
const isSelectable = (r) => !!r && !r.runId;

class TruckReceiptsGrid extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            receipts: [],
            loading: false,
            dateFrom: defaultDateFrom(),
            dateTo: defaultDateTo(),
            archiveFilter: ARCHIVE_FILTERS.ALL,
            selectedIds: [],
            emailModalOpen: false,
            emailModalContent: '',
        };

        this.loadReceipts = this.loadReceipts.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.handleReset = this.handleReset.bind(this);
        this.handleArchiveFilterChange = this.handleArchiveFilterChange.bind(this);
        this.getColumns = this.getColumns.bind(this);
        this.toggleSelect = this.toggleSelect.bind(this);
        this.toggleSelectAll = this.toggleSelectAll.bind(this);
        this.archiveSelected = this.archiveSelected.bind(this);
        this.openEmailModal = this.openEmailModal.bind(this);
        this.closeEmailModal = this.closeEmailModal.bind(this);
    }

    openEmailModal(content) {
        this.setState({ emailModalOpen: true, emailModalContent: content || '' });
    }

    closeEmailModal() {
        this.setState({ emailModalOpen: false, emailModalContent: '' });
    }

    componentDidMount() {
        this.loadReceipts();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.numberPlate !== this.props.numberPlate) {
            this.setState({
                dateFrom: defaultDateFrom(),
                dateTo: defaultDateTo(),
                selectedIds: [],
            }, this.loadReceipts);
        }
    }

    loadReceipts() {
        const { numberPlate } = this.props;
        const { dateFrom, dateTo, archiveFilter } = this.state;

        if (!numberPlate) {
            return;
        }

        const params = new URLSearchParams();
        params.append('numberPlate', numberPlate);
        if (dateFrom) {
            params.append('from', dateFrom);
        }
        if (dateTo) {
            params.append('to', dateTo);
        }
        if (archiveFilter === ARCHIVE_FILTERS.UNALLOCATED) {
            params.append('isArchived', 'false');
            params.append('isAllocated', 'false');
        } else if (archiveFilter === ARCHIVE_FILTERS.ARCHIVED) {
            params.append('isArchived', 'true');
        }

        this.setState({ loading: true });
        apiRequest(`/api/trucks/receipts?${params.toString()}`).then((res) => {
            if (res && res.data && Array.isArray(res.data)) {
                this.setState({ receipts: res.data, loading: false });
            } else {
                this.setState({ receipts: [], loading: false });
            }
        });
    }

    handleSearch() {
        this.setState({ selectedIds: [] }, this.loadReceipts);
    }

    handleReset() {
        this.setState({
            dateFrom: defaultDateFrom(),
            dateTo: defaultDateTo(),
            archiveFilter: ARCHIVE_FILTERS.ALL,
            selectedIds: [],
        }, this.loadReceipts);
    }

    handleArchiveFilterChange(e) {
        this.setState({ archiveFilter: e.target.value, selectedIds: [] }, this.loadReceipts);
    }

    toggleSelect(id) {
        const { selectedIds } = this.state;
        this.setState({
            selectedIds: selectedIds.indexOf(id) > -1
                ? selectedIds.filter(x => x !== id)
                : selectedIds.concat([id]),
        });
    }

    toggleSelectAll() {
        const selectable = mustBeArray(this.state.receipts).filter(isSelectable).map(r => r.id);
        this.setState({
            selectedIds: this.state.selectedIds.length === selectable.length ? [] : selectable,
        });
    }

    // Archive (or restore) the selected receipts. Only the rows that can actually change are sent —
    // the server applies the same rules and reports back anything it skipped.
    archiveSelected(archive) {
        const { receipts, selectedIds } = this.state;
        const receiptIds = mustBeArray(receipts)
            .filter(r => selectedIds.indexOf(r.id) > -1
                && isSelectable(r)
                && (archive ? !r.archivedDate : !!r.archivedDate))
            .map(r => r.id);

        if (!receiptIds.length) {
            return;
        }

        this.setState({ loading: true });
        apiPost('/api/trucks/receipts/archive', { receiptIds, archive }).then((res) => {
            const result = (res && res.data) || {};
            if (res && res.error) {
                toastr.error('Receipts', 'Could not update those receipts.');
            } else if (result.skipped) {
                toastr.warning('Receipts', `${result.updated} updated, ${result.skipped} skipped (already allocated to a run, or already in that state).`);
            } else {
                toastr.success('Receipts', `${result.updated} receipt${result.updated === 1 ? '' : 's'} ${archive ? 'archived' : 'restored'}.`);
            }
            this.setState({ selectedIds: [] }, this.loadReceipts);
        });
    }

    getColumns() {
        const { receipts, selectedIds } = this.state;
        const selectableCount = mustBeArray(receipts).filter(isSelectable).length;

        return [
            {
                id: 'select',
                Header: () => (
                    <input
                        type="checkbox"
                        aria-label="Select all unallocated receipts"
                        checked={selectableCount > 0 && selectedIds.length === selectableCount}
                        disabled={selectableCount === 0}
                        onChange={this.toggleSelectAll}
                    />
                ),
                sortable: false,
                width: 40,
                accessor: x => x.id,
                // Allocated receipts have no checkbox — they can't be archived.
                Cell: row => {
                    const r = row && row.original;
                    if (!isSelectable(r)) {
                        return '';
                    }
                    return (
                        <input
                            type="checkbox"
                            aria-label="Select receipt"
                            checked={selectedIds.indexOf(r.id) > -1}
                            onChange={() => this.toggleSelect(r.id)}
                        />
                    );
                },
            },
            {
                id: 'receivedAt',
                Header: 'Received',
                width: 140,
                accessor: x => x.receivedAt,
                Cell: row => formatDateTime(row && row.value),
            },
            {
                id: 'type',
                Header: 'Type',
                accessor: x => x.type,
                width: 90,
                Cell: row => (row && row.value) || 'Delivery',
            },
            {
                id: 'deliveryTotal',
                Header: 'Volume',
                accessor: x => (x.litres != null ? x.litres : x.deliveryTotal),
                width: 80,
                Cell: row => formatLitres(row && row.value),
            },
            {
                id: 'tankId',
                Header: 'Tank #',
                accessor: x => x.tankId,
                width: 80,
            },
            {
                id: 'runId',
                Header: 'Run',
                accessor: x => x.runId,
                width: 80,
                Cell: row => {
                    const runId = row && row.value;
                    if (runId) {
                        return (
                            <a href={`${BASE_URL}/runs/search/${runId}`} target="_blank" rel="noopener noreferrer">
                                #{runId}
                            </a>
                        );
                    }
                    return '';
                },
            },            
            {
                id: 'pickup',
                Header: 'Pickup',
                sortable: false,
                width: 80,
                accessor: x => x.pickupId,
                Cell: row => {
                    const r = row && row.original;
                    if (r && r.runId && r.locationId) {
                        return (
                            <a href={`${BASE_URL}/runs/search/${r.runId}/locations/${r.locationId}`} target="_blank" rel="noopener noreferrer">
                                View
                            </a>
                        );
                    }
                    return '';
                },
            },
            {
                id: 'driverName',
                Header: 'Driver',
                accessor: x => x.driverName,
                Cell: row => (row && row.value) || '',
            },            
            {
                id: 'address',
                Header: 'Address',
                accessor: x => x.address,
                Cell: row => (row && row.value) || '',
            },
            {
                id: 'location',
                Header: 'Lat / Long',
                sortable: false,
                accessor: x => x.latitude,
                width: 160,
                Cell: row => {
                    const r = row && row.original;
                    if (r && (r.latitude || r.longitude)) {
                        return (
                            <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`} target="_blank" rel="noopener noreferrer">
                                {parseFloat(r.latitude).toFixed(3)}, {parseFloat(r.longitude).toFixed(3)}
                            </a>
                        );
                    }
                    return '';
                },
            },
            {
                id: 'emailContent',
                Header: 'Receipt',
                sortable: false,
                maxWidth: 80,
                accessor: x => x.emailContent,
                Cell: row => {
                    const content = row && row.value;
                    if (content) {
                        return (
                            <a href="#" onClick={(e) => { e.preventDefault(); this.openEmailModal(content); }}>
                                View
                            </a>
                        );
                    }
                    return '';
                },
            },
            {
                id: 'archivedDate',
                Header: 'Archived',
                width: 150,
                accessor: x => x.archivedDate,
                Cell: row => {
                    const r = row && row.original;
                    if (!r || !r.archivedDate) {
                        return '';
                    }
                    return (
                        <span title={r.archivedByName ? `Archived by ${r.archivedByName}` : ''}>
                            <i className="fa fa-archive" aria-hidden="true" />&nbsp;{formatDateTime(r.archivedDate)}
                        </span>
                    );
                },
            }
        ];
    }

    render() {
        const { receipts, loading, dateFrom, dateTo, archiveFilter, selectedIds,
            emailModalOpen, emailModalContent } = this.state;

        const selected = mustBeArray(receipts).filter(r => selectedIds.indexOf(r.id) > -1 && isSelectable(r));
        const canArchive = selected.some(r => !r.archivedDate);
        const canRestore = selected.some(r => !!r.archivedDate);

        return (
            <div>
                <Row className="align-items-center mb-2 full-width-datepicker">
                    <Col sm={3} md={1}>
                        <Label for="from" className="mb-0">From</Label>
                    </Col>
                    <Col sm={9} md={3} className="mb-1">
                        <DatePicker
                            onChange={(date) => this.setState({ dateFrom: date ? moment(date).format(DEFAULT_DATE_FORMAT) : null })}
                            value={dateFrom || undefined}
                            openToDate={dateFrom ? moment(dateFrom) : moment()}
                            placeholderText="Date From"
                            locale="en-au"
                            isClearable
                            className="form-control"
                        />
                    </Col>
                    <Col sm={3} md={1}>
                        <Label for="to" className="mb-0">To</Label>
                    </Col>
                    <Col sm={9} md={3} className="mb-1">
                        <DatePicker
                            onChange={(date) => this.setState({ dateTo: date ? moment(date).format(DEFAULT_DATE_FORMAT) : null })}
                            value={dateTo || undefined}
                            openToDate={dateTo ? moment(dateTo) : moment()}
                            placeholderText="Date To"
                            locale="en-au"
                            isClearable
                            minDate={dateFrom ? moment(dateFrom) : undefined}
                            className="form-control"
                        />
                    </Col>
                    <Col sm={12} md={4} className="mb-1">
                        <button className="btn btn-primary" onClick={this.handleSearch}>
                            <i className="fa fa-search" aria-hidden="true" />&nbsp;Search
                        </button>
                        <button className="btn btn-default ml-1" onClick={this.handleReset}>Reset</button>
                    </Col>
                </Row>
                <Row className="align-items-center mb-1">
                    <Col sm={3} md={1}>
                        <Label for="archiveFilter" className="mb-0">Show</Label>
                    </Col>
                    <Col sm={9} md={3} className="mb-1">
                        <Input type="select" id="archiveFilter" value={archiveFilter}
                               onChange={this.handleArchiveFilterChange}>
                            <option value={ARCHIVE_FILTERS.ALL}>All receipts</option>
                            <option value={ARCHIVE_FILTERS.UNALLOCATED}>Unallocated, not archived</option>
                            <option value={ARCHIVE_FILTERS.ARCHIVED}>Archived only</option>
                        </Input>
                    </Col>
                    {/* Spacer so the actions line up with Search/Reset in the row above. */}
                    <Col md={4} className="d-none d-md-block" />
                    <Col sm={12} md={4} className="mb-1">
                        <Button color="secondary" disabled={!canArchive} onClick={() => this.archiveSelected(true)}>
                            <i className="fa fa-archive" aria-hidden="true" />&nbsp;Archive selected
                        </Button>
                        <Button color="secondary" outline className="ml-1" disabled={!canRestore}
                                onClick={() => this.archiveSelected(false)}>
                            <i className="fa fa-undo" aria-hidden="true" />&nbsp;Restore selected
                        </Button>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col sm={12}>
                        <small className="text-muted">
                            Archived receipts stay listed here but are hidden from the driver screens.
                            Receipts already allocated to a run can&apos;t be archived.
                        </small>
                    </Col>
                </Row>
                <ReactTable
                    sortable
                    filterable={false}
                    loading={loading}
                    showPagination
                    defaultPageSize={10}
                    data={mustBeArray(receipts)}
                    columns={this.getColumns()}
                    minRows={mustBeArray(receipts).length === 0 ? 5 : Math.min(mustBeArray(receipts).length, 10)}
                    className="br-1"
                    noDataText="No receipts for this truck"
                    getTrProps={(state, rowInfo) => (
                        rowInfo && rowInfo.original && rowInfo.original.archivedDate
                            ? { className: 'text-muted' }
                            : {}
                    )}
                />
                <Modal isOpen={emailModalOpen} toggle={this.closeEmailModal} size="lg">
                    <ModalHeader toggle={this.closeEmailModal}>Receipt Email Content</ModalHeader>
                    <ModalBody>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 0 }}>{emailModalContent}</pre>
                        <div className="mt-2 text-right">
                            <Button color="secondary" onClick={this.closeEmailModal}>Close</Button>
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        );
    }
}

export default TruckReceiptsGrid;
